import { UserManager, type UserManagerSettings } from 'oidc-client-ts'
import { inject, type App } from 'vue'

declare module 'vue' {
  interface ComponentCustomProperties {
    $userManager: UserManager
  }
}

const UserManagerKey = Symbol('UserManager')
let globalUserManager: UserManager | null = null

/**
 * Creates a `UserManager` plugin (oidc-auth-ts).
 * @param {UserManagerSettings} settings - additional `UserManager` settings.
 * @returns - new UserManager plugin.
 */
export function createUserManagerPlugin(settings: UserManagerSettings) {
  const userManager = new UserManager({
    ...settings,
  })

  globalUserManager = userManager

  return {
    install(app: App) {
      // Provide for composition API
      app.provide(UserManagerKey, userManager)

      // Attach for Options API
      app.config.globalProperties.$userManager = userManager
    },
  }
}

/**
 * Helper composable for `UserManager`.
 * @returns {UserManager} - the current `UserManager`.
 * @throws {Error} - if the `UserManager` was not provided.
 */
export function useUserManager(): UserManager {
  return inject<UserManager>(UserManagerKey) ?? getUserManager()
}

/**
 * Getter for use outside setup or functional components.
 * @returns {UserManager} - the global `UserManager` instance.
 */
export function getUserManager(): UserManager {
  if (!globalUserManager) {
    throw new Error('UserManager not provided. Did you forget to install the plugin?')
  }
  return globalUserManager
}
